import { useCallback, useMemo } from 'react';
import { DepartmentsQueries } from '@frontend/api-departments';
import { PhoneNumbersV1 } from '@frontend/api-phone-numbers';
import { useTranslation } from '@frontend/i18n';
import { formatPhoneNumberE164 } from '@frontend/phone-numbers';
import { useAppScopeStore } from '@frontend/scope';
import { useAppFlagStore } from '@frontend/shared';

type UseDepartmentLocationPhoneMappingsArgs = {
  enabled?: boolean;
};

type MigrationStage = 'stage-0' | 'stage-1' | 'stage-2';
type DepartmentData = {
  locationId: string;
  locationPhone: string;
  departmentName: string;
  departmentId: string;
  phoneName: string;
};

/**
 * Custom hook that manages department to locationPhone mappings and handles migration stages
 * for the department deprecation process.
 *
 * @param enabled (optional) - Default `true`. If false, disables queries and data fetching inside the hook
 *
 * This hook provides utilities for:
 * - Mapping department IDs to their associated location phone numbers
 * - Determining migration stages based on feature flags
 * - Switching between department IDs and location phones based on migration stage
 *
 * The hook supports a multi-stage migration process:
 * - **Stage 0**: Uses department IDs (legacy behavior)
 * - **Stage 1**: Begins transitioning to location phones limited to departments (`deprecate-departments` FF)
 * - **Stage 2**: Fully migrated to all available location phones (`phone-numbers-name-id` FF)
 *
 * @returns An object containing utility functions:
 * - getDataFromLocationPhone - Function that retrieves outbound phone data based on a location phone number
 * - getDataFromDepartmentId - Function that retrieves outbound phone data based on a departmentId
 * - getFFDependentDepartmentIdOrLocationPhone - Function that maps departmentIds to either departmentIds or
 *   locationPhones based on migration stage and feature flags
 * - data - An array of all department data objects, each containing:
 *   - locationId
 *   - departmentId
 *   - departmentName
 *   - locationPhone (in E164 format)
 */
export const useDepartmentLocationPhoneMappings = ({ enabled = true }: UseDepartmentLocationPhoneMappingsArgs = {}) => {
  const { t } = useTranslation('inbox-utils');
  const { selectedLocationIds } = useAppScopeStore();
  const { getFeatureFlagValue } = useAppFlagStore();
  const defaultSMSQueries = DepartmentsQueries.useListDefaultSMSQueries(
    selectedLocationIds.map((locationId) => ({
      req: { locationId },
      options: { enabled },
    }))
  );
  const locationsWithoutDepartments = useMemo<string[]>(() => {
    return defaultSMSQueries.reduce<string[]>((acc, query) => {
      if (!!query.data && !query.data.smsNumbers?.length) {
        acc.push(query.data?.locationId);
      }
      return acc;
    }, []);
  }, [defaultSMSQueries]);
  const smsPhoneNumbersQueries = PhoneNumbersV1.Queries.useListSMSPhoneNumbersQueries(
    locationsWithoutDepartments.map((locationId) => ({
      request: {},
      options: {
        select: (data) => ({
          ...data,
          locationId,
          sms_numbers_response: data.sms_numbers_response?.filter((number) => !!number.active) ?? [],
        }),
        enabled,
      },
      httpOptions: { headers: { 'Location-Id': locationId } },
    }))
  );
  const { departmentsMap, locationPhonesMap, data } = useMemo(
    () =>
      defaultSMSQueries.reduce<{
        departmentsMap: Record<string, DepartmentData>;
        locationPhonesMap: Record<string, DepartmentData>;
        data: DepartmentData[];
      }>(
        (acc, query) => {
          if (!query.data || !query.data.locationId) return acc;

          if (!query.data.smsNumbers?.length) {
            const matchingSMSPhoneQuery = smsPhoneNumbersQueries.find(
              (q) => q.data?.locationId === query.data.locationId
            );
            if (!matchingSMSPhoneQuery?.data) return acc;
            const entries = matchingSMSPhoneQuery.data.sms_numbers_response.map<DepartmentData>((number) => ({
              departmentId: '',
              locationPhone: formatPhoneNumberE164(number.number ?? ''),
              departmentName: '',
              locationId: query.data.locationId,
              phoneName: number.displayName ?? '',
            }));
            entries.forEach((entry) => {
              acc.locationPhonesMap[entry.locationPhone] = entry;
              acc.data.push(entry);
            });
            return acc;
          }

          return (
            query.data.smsNumbers?.reduce<{
              departmentsMap: Record<string, DepartmentData>;
              locationPhonesMap: Record<string, DepartmentData>;
              data: DepartmentData[];
            }>((acc, department) => {
              const locationPhone = department.smsNumber?.number;
              const departmentName = department.name || t('Untitled Department');
              const phoneName = department.smsNumber?.name || '';
              if (!locationPhone || !departmentName || !department.id) return acc;

              const formattedPhone = formatPhoneNumberE164(locationPhone);
              const entry = {
                departmentId: department.id,
                locationPhone: formattedPhone,
                departmentName,
                locationId: query.data.locationId,
                phoneName,
              };

              return {
                departmentsMap: {
                  ...acc.departmentsMap,
                  [department.id]: entry,
                },
                locationPhonesMap: {
                  ...acc.locationPhonesMap,
                  [formattedPhone]: entry,
                },
                data: [...acc.data, entry],
              };
            }, acc) ?? acc
          );
        },
        { departmentsMap: {}, locationPhonesMap: {}, data: [] }
      ),
    [defaultSMSQueries, smsPhoneNumbersQueries, t]
  );

  const getStageFromLocationIds = useCallback(
    (locationIds: string[]): MigrationStage => {
      const allLocationsAreOnStage1 = getFeatureFlagValue({
        locationIds,
        flagName: 'deprecate-departments',
        strategy: 'ALL',
      });
      if (!allLocationsAreOnStage1) {
        return 'stage-0';
      }

      const allLocationsAreOnStage2 = getFeatureFlagValue({
        locationIds,
        flagName: 'phone-numbers-name-id',
        strategy: 'ALL',
      });

      if (!allLocationsAreOnStage2) {
        return 'stage-1';
      }

      return 'stage-2';
    },
    [getFeatureFlagValue]
  );

  const getFFDependentDepartmentIdOrLocationPhone = useCallback(
    <T = { mappedValues: string[]; valueType: 'locationPhone' | 'departmentId' }>({
      departmentIds,
      stageForSwitchingToLocationPhone,
      select = (data) => data as T,
    }: {
      departmentIds: string[];
      stageForSwitchingToLocationPhone: Exclude<MigrationStage, 'stage-0'>;
      select?: (data: { mappedValues: string[]; valueType: 'locationPhone' | 'departmentId' }) => T;
    }): T => {
      if (departmentIds.length === 0) {
        return select({
          mappedValues: [],
          valueType: 'departmentId',
        });
      }
      const locationIds = departmentIds.reduce<Set<string>>((acc, departmentId) => {
        const locationId = departmentsMap[departmentId]?.locationId;
        if (locationId) {
          acc.add(departmentsMap[departmentId]?.locationId);
        }
        return acc;
      }, new Set<string>());
      const currentStage = getStageFromLocationIds(Array.from(locationIds));

      if (currentStage === 'stage-0') {
        return select({
          mappedValues: departmentIds,
          valueType: 'departmentId',
        });
      }

      const locationPhones = departmentIds.reduce<string[]>((acc, departmentId) => {
        const locationPhone = departmentsMap[departmentId]?.locationPhone;
        if (locationPhone) {
          acc.push(locationPhone);
        }
        return acc;
      }, []);

      switch (stageForSwitchingToLocationPhone) {
        case 'stage-1':
          return select({
            mappedValues: locationPhones,
            valueType: 'locationPhone',
          });
        case 'stage-2':
          return currentStage === 'stage-2'
            ? select({
                mappedValues: locationPhones,
                valueType: 'locationPhone',
              })
            : select({
                mappedValues: departmentIds,
                valueType: 'departmentId',
              });
      }
    },
    [departmentsMap, getStageFromLocationIds]
  );

  const getDataFromLocationPhone = useCallback(
    (locationPhone: string) => {
      return locationPhonesMap[formatPhoneNumberE164(locationPhone)];
    },
    [locationPhonesMap]
  );

  const getDataFromDepartmentId = useCallback(
    (departmentId: string) => {
      return departmentsMap[departmentId];
    },
    [departmentsMap]
  );

  return {
    /**
     * Gets outbound phone data based on the provided locationPhone number.
     *
     * @param locationPhone - The location phone number. This will automatically be formatted to E164 for the lookup.
     * @returns The outbound phone data associated with the provided location phone number, or undefined if not found.
     * - departmentId: The ID of the department
     * - locationPhone: The phone number of the location in E164 format
     * - departmentName: The name of the department
     * - locationId: The ID of the location associated with the department
     */
    getDataFromLocationPhone,
    /**
     * Gets outbound phone data based on the provided departmentId.
     *
     * @param departmentId - The departmentId to look up.
     * @returns The outbound phone data associated with the provided location phone number, or undefined if not found.
     * - departmentId: The ID of the department
     * - locationPhone: The phone number of the location in E164 format
     * - departmentName: The name of the department
     * - locationId: The ID of the location associated with the department
     */
    getDataFromDepartmentId,
    /**
     * Maps departmentIds to either departmentIds or locationPhone numbers based on
     * the current migration stage and feature flag configuration.
     *
     * This function is central to the department deprecation migration process. It determines
     * whether to return departmentIds (legacy behavior) or locationPhone numbers (new behavior)
     * based on the current migration stage of the locations associated with the provided departments.
     *
     * @param departmentIds - Array of departmentIds to be mapped
     * @param stageForSwitchingToLocationPhone - The migration stage at which the function
     * should switch from returning departmentIds to location phone numbers. Must be either
     * 'stage-1' or 'stage-2'. Cannot be 'stage-0' as that stage always returns departmentIds.
     *
     * @returns An object containing:
     * - mappedValues: Array of either department IDs or location phone numbers in E164 format
     * - valueType: Indicates whether the returned values are 'departmentId' or 'locationPhone'
     */
    getFFDependentDepartmentIdOrLocationPhone,
    data,
  };
};
